import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleFigureCaption,
  articleImageStyle,
  bold,
  faqGrid,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  infoWrap,
  orderedList,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { vars } from "~/components/styles/theme.css"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"

// SEO FIX: Title shortened from 73 to 52 chars (was over 60 char limit)
const TITLE = "Japanese Zen Garden Plants & Materials Guide"
const DESCRIPTION =
  "Discover how to design a Japanese Zen garden that inspires peace and mindfulness. Learn about gravel patterns, rock placement, bamboo, moss, and plant choices."
const IMAGE = "https://rescapeai.com/guide/red-maple-zen.webp"
const URL = "https://rescapeai.com/garden-design-guide/zen/materials-and-plants"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-30",
        dateModified: "2025-10-30",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/japanese-zen-1.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Japanese Zen Gardens: A Guide to Creating Tranquility and Balance
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Japanese Zen gardens embrace simplicity, harmony, and mindfulness. Unlike
            traditional gardens focused on lush planting or colourful displays, Zen
            gardens use minimalism, symbolism, and natural elements to evoke peace and
            contemplation. Whether you have a spacious yard or a compact outdoor corner,
            this style is perfectly adaptable to any size, transforming your space into a
            serene retreat.
          </p>
          <p className={articleBodyStyle}>
            Characterised by raked gravel, carefully placed rocks, subtle greenery, and
            thoughtful structure, Zen gardens cultivate calm through restraint. Let's
            explore how you can create your own Japanese Zen-inspired sanctuary.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Clean Lines and Symbolic Layouts</h2>
          <p className={articleBodyStyle}>
            Zen garden design is guided by geometry and purposeful placement. Just as
            contemporary gardens use sharp straight lines, Zen gardens rely on balance and
            asymmetry to evoke nature in an abstract way.{" "}
            <Link href="/garden-design-guide/zen/features-of-zen-gardens">
              Gravel or sand represents water, while rocks signify mountains or islands.
            </Link>
          </p>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/zen-rocks.webp"
              alt="Raked sand and rocks in a traditional Japanese Zen garden layout"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              A peaceful and contemplative retreat with sand and rocks
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Start with your garden's boundary. Simple timber panels or bamboo fences
            create a neutral backdrop. Choose natural wood tones or muted stains—avoid
            bright colours that distract from the garden's meditative intent.
          </p>
          <p className={articleBodyStyle}>
            Rounded forms are also essential. Unlike strictly straight-edged contemporary
            spaces, Zen gardens often feature circular gravel arrangements, softly curved
            stepping stones, or round moss patches. These subtle curves introduce flow and
            embody the concept of ensō—a Zen symbol of enlightenment and unity.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Rock and Gravel: The Heart of Zen Design</h2>
          <p className={articleBodyStyle}>
            Raked gravel or sand is one of the defining elements of a Zen garden. When
            raked into patterns—such as straight lines, ripples, or concentric circles—it
            symbolises flowing water or waves. The act of raking itself is a form of
            meditation, encouraging focus and calm.
          </p>
          <h3 className={h3Style}>Tips:</h3>
          <ul className={unorderedList}>
            <li>Use light-coloured gravel or fine sand.</li>
            <li>Rake consistently to reinforce visual harmony.</li>
            <li>
              Position rocks in odd-numbered groupings (e.g., sets of three or five) to
              create natural-looking compositions.
            </li>
          </ul>
          <p className={articleBodyStyle}>
            Large stones act as focal points, representing mountains or spiritual anchors.
            Their placement should feel intentional yet organic, blending naturally into
            the landscape.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Wood, Stone, and Natural Materials</h2>
          <p className={articleBodyStyle}>
            In Japanese Zen gardens, materials are selected for their simplicity and
            connection to nature.
          </p>
          <p className={articleBodyStyle}>
            Wooden walkways, bamboo fences, and stone lanterns (called tōrō) bring subtle
            elegance without overwhelming the space. Unlike contemporary gardens where
            concrete is common, Zen gardens prioritise natural textures that age
            gracefully with time.
          </p>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/zen-garden-arch.webp"
              alt="Wooden walkway and arch in a Japanese Zen garden setting"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              A wooden walkway that will weather with time
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Avoid brightly painted features. Instead, allow patina, weathering, and
            natural textures to reflect the passage of time—a concept known as wabi-sabi,
            which celebrates beauty in imperfection.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Creating Quiet Seating Spaces</h2>
          <p className={articleBodyStyle}>
            Much like contemporary pergolas that create a relaxation zone, Zen gardens
            include quiet corners designed for stillness. These may be simple wooden
            benches beside raked gravel areas or a platform overlooking a tranquil
            composition of rocks and moss.
          </p>
          <p className={articleBodyStyle}>
            Seating should be unobtrusive—think low wooden benches, stone platforms, or
            tatami-style decked areas. The idea is to create a peaceful spot for
            meditation, tea rituals, or simply appreciating stillness.
          </p>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/zen-wooden-bench.webp"
              alt="Low wooden bench set within a minimalist Zen garden space"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              A wooden bench set within a mindful space
            </figcaption>
          </figure>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Plant Selection for Zen Gardens</h2>
          <p className={articleBodyStyle}>
            Planting in Zen gardens is minimal and meaningful. Rather than a colourful
            display, plants are used selectively to express calm and seasonal change.
            Stick to a restrained palette of greens and textures rather than bold flowers.
          </p>
          <p className={articleBodyStyle}>
            There are two main approaches:
            <ol className={orderedList}>
              <li>
                <strong className={bold}>
                  Sparse planting with sculptural precision -
                </strong>{" "}
                emphasising space and simplicity.
              </li>
              <li>
                <strong className={bold}>Mossy naturalism –</strong> soft, carpet-like
                coverage to evoke forest quiet.
              </li>
            </ol>
          </p>
          <h2 className={h2Style}>Essential Zen Garden Plants</h2>
          <h3 className={h3Style}>Bamboo</h3>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/bamboo-zen.webp"
              alt="Bamboo plants used for privacy and vertical interest in a Zen garden"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              Bamboo used within a garden
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Bamboo offers gentle movement and the soft sound of rustling leaves in the
            wind, promoting a contemplative atmosphere. Use clumping varieties to maintain
            control and plant them along borders or near seating areas to create privacy
            and vertical interest.
          </p>
          <h3 className={h3Style}>Japanese Maple (Acer palmatum)</h3>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/japanese-maple.webp"
              alt="Japanese Maple Acer palmatum with delicate seasonal foliage"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              A beautiful Japanese Maple Tree (Acer palmatum)
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            With delicate leaves that change colour seasonally, Japanese maples bring
            quiet elegance. Their fine branching structure makes them striking focal
            points without disrupting the garden's calm aesthetic.
          </p>
          <h3 className={h3Style}>Moss</h3>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/moss.webp"
              alt="Moss ground cover creating a velvety carpet in a Zen garden"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              Moss used gracefully in a Zen garden
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Moss is often used instead of grass, thriving in shaded areas and creating a
            lush, velvety carpet. It enhances serenity and absorbs sound, reinforcing the
            quiet tone of the garden.
          </p>
          <h3 className={h3Style}>
            Evergreen Shrubs (e.g., Boxwood or Camellia japonica)
          </h3>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/zen-shrub.webp"
              alt="Camellia japonica evergreen shrub pruned into cloud shapes for Zen garden"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              Camellia japonica adds beauty with it's colourful flowers
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Pruned into rounded or cloud-like shapes, evergreens contribute structure and
            year-round tranquillity. Their controlled forms reflect the discipline and
            mindfulness central to Zen philosophy.
          </p>
          <h3 className={h3Style}>Ornamental Grasses (e.g., Hakone grass)</h3>
          <figure style={{ marginBottom: "2rem" }}>
            <img
              src="/guide/zen-grasses.webp"
              alt="Ornamental Hakone grasses adding soft movement to a Zen garden"
              className={articleImageStyle}
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={articleFigureCaption}>
              Grasses add texture and movement
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            Soft, flowing ornamental grasses bring movement without adding clutter. Their
            pale green tones complement rocks and gravel while maintaining a minimal feel.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Seasonal Interest Without Disruption</h2>
          <p className={articleBodyStyle}>
            Where a contemporary garden may use flowers like Alliums and Salvias, Zen
            gardens rely on subtle seasonal shifts. The gentle bloom of a single cherry
            blossom tree or the autumn flare of an Acer is enough to evoke emotion without
            overwhelming the space.
          </p>
          <p className={articleBodyStyle}>
            <strong className={bold}>Recommended seasonal highlights:</strong>
            <ol className={orderedList}>
              <li>
                <strong className={bold}>Cherry blossoms (Sakura)</strong> for spring
                grace
              </li>
              <li>
                <strong className={bold}>Azaleas</strong> in soft pink or white for
                refined colour
              </li>
              <li>
                <strong className={bold}>Ferns</strong> for lush summer greenery
              </li>
              <li>
                <strong className={bold}>Japanese forest grass</strong> for golden autumn
                tones
              </li>
            </ol>
            Each plant should be carefully chosen for its shape, seasonal story, and
            connection to nature.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Key Takeaways</h2>
          <ol className={orderedList}>
            <li>
              Zen gardens embody simplicity, balance, and contemplation through minimal
              design and symbolic elements.
            </li>
            <li>
              Gravel, sand, and rocks form the foundation, representing water and
              mountains in abstract, meditative compositions.
            </li>
            <li>
              Natural materials like bamboo, wood, and stone create an authentic, timeless
              setting.
            </li>
            <li>
              Seating areas should be quiet, unobtrusive spaces intended for reflection or
              tea ceremonies.
            </li>
            <li>
              Plant choice should be minimal, focusing on greens, sculptural forms, and
              seasonal subtlety.
            </li>
            <li>
              Ideal plants include bamboo, moss, Japanese maples, evergreen shrubs, and
              ornamental grasses.
            </li>
            <li>
              The style suits any garden size, from expansive landscapes to small
              courtyards or even balcony Zen corners.
            </li>
            <li>
              With careful curation, a Zen garden becomes a living space for calm,
              mindfulness, and quiet beauty.
            </li>
          </ol>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style} id="faq">
            Frequently Asked Questions
          </h2>
          <div className={faqGrid}>
            <div>
              <h3 className={h3Style}>
                How much does it typically cost to create a Japanese Zen garden, and what
                elements should be prioritised on a budget?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                A Japanese Zen garden can cost from £100–£500 for a small DIY setup to
                £2,000+ with professional installation. To stay on budget, prioritise core
                elements that define the style: gravel or sand for the "water" surface, a
                few carefully placed large rocks, and minimal plants like bamboo, moss, or
                a small Japanese maple. Simple bamboo screens or wooden borders add
                structure affordably.
              </p>
            </div>
            <div>
              <h3 className={h3Style}>
                Are there specific tools or techniques recommended for maintaining gravel
                raking patterns in a Zen garden?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                To maintain gravel raking patterns in a Zen garden, a wide-toothed wooden
                or metal rake is typically used, with wooden rakes preferred for softer,
                more refined designs. The technique involves applying even pressure and
                raking from the edges inward to create smooth lines or ripples
              </p>
            </div>
            <div>
              <h3 className={h3Style}>
                What cultural or historical principles from Zen Buddhism influence the
                traditional layout and symbolism of these gardens?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Zen gardens are deeply influenced by Zen Buddhist principles such as
                simplicity (kanso), tranquility (seijaku), and wabi-sabi, which embraces
                imperfection and the passage of time. Their layouts are designed to
                encourage meditation and mindfulness, with rocks symbolising mountains or
                islands and gravel representing water or the flow of life.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup">AI Garden Design</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
